import React from "react";
import StatusBadge from "./StatusBadge";
import { useNavigate } from "react-router-dom";
import { useSort } from "../../context/SortContext";
import EditIcon from "../../assets/icons/edit";
import { convertAmountToPretty, strToFormattedDate } from "../../helpers/formatHelper";
import { getColumnByType } from "../../helpers/tableHelper";

export default function Table({ columns, data, clickableRow, showIndex = false, showSum = false }) {
  const { sortConfig, setSortConfig } = useSort();
  const navigate = useNavigate();
  const hasClickableRow = Boolean(clickableRow);

  const handleSort = (column) => {
    const key = column.sortKey || column.key;
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === "asc" ? "desc" : "asc",
    }));
  };

  const removeSort = (event) => {
    event.stopPropagation();
    setSortConfig({ key: null, direction: null });
  };

  const renderCellContent = (value, type) => {
    if (value === null || value === undefined) return "-";

    switch (type) {
      case getColumnByType.STATUS:
        return <StatusBadge text={value.text} color={value.color} />;
      case getColumnByType.BUTTON:
        return (
          <button onClick={(event) => {
            event.stopPropagation();
            navigate(value);
          }}
          >
            <div className="w-8 h-auto"><EditIcon /></div>
          </button>
        );
      case getColumnByType.AMOUNT:
        return convertAmountToPretty(value);
      case getColumnByType.DATE:
        return strToFormattedDate(value, "dd/MM/yyyy");
      case getColumnByType.DATETIME:
        return strToFormattedDate(value, "dd/MM/yyyy hh:mm");
      case getColumnByType.PERCENT:
        return <span className={value > 100 ? "text-red-400" : ""}>{value} %</span>;
      default:
        return value;
    }
  };

  const calculateColumnTotal = (columnKey) => {
    return data.reduce((total, row) => {
      const value = row[columnKey];
      return total + (typeof value === "number" ? value : 0);
    }, 0);
  };

  return (
    <div className="overflow-x-auto no-scrollbar min-w-full">
      <table className="min-w-full table-auto">
        <thead>
          <tr>
            {(showIndex || showSum) && <th key="index"></th>}
            {columns && columns.map(column => (
              <th
                key={column.key}
                onClick={() => column.sortable && handleSort(column)}
                className={`px-4 py-2 text-left ${column.sortable ? "cursor-pointer hover:bg-gray-200" : ""}`}
              >
                <div className="flex items-center">
                  {column.title}
                  {column.sortable && sortConfig.key === (column.sortKey || column.key) && (
                    <>
                      <span className="ml-2 text-sm">{sortConfig.direction === "asc" ? "▲" : "▼"}</span>
                      <span className="ml-2 cursor-pointer text-xs text-gray-500 hover:text-gray-700 bg-gray-100 px-1 rounded-full" onClick={removeSort}>
                        ✖
                      </span>
                    </>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data && data.map((row, rowIndex) => (
            <React.Fragment key={row.id || rowIndex}>
              <tr
                className={`text-gray-800 border-y-2 border-gray-100 hover:bg-gray-100 ${hasClickableRow ? "cursor-pointer" : ""}`}
                {...(hasClickableRow && { onClick: () => navigate(row[clickableRow.key]) })}
              >
                {(showIndex || showSum) && <td className="text-center">{rowIndex + 1}</td>}
                {columns && columns.map((column, columnIndex) => (
                  <td key={column.key} className={`px-4 py-2 ${columnIndex === 0 ? "font-semibold" : ""}`}>
                    {renderCellContent(row[column.key], column.type)}
                  </td>
                ))}
              </tr>
            </React.Fragment>
          ))}
          {showSum && (
            <tr>
              <td className="text-center font-bold">Total</td>
              {columns && columns.map(column => (
                column.type === getColumnByType.AMOUNT || column.type === getColumnByType.NUMBER || column.type === getColumnByType.PERCENT
? (
                  <td key={column.key} className="px-4 py-2 font-semibold">
                    {renderCellContent(calculateColumnTotal(column.key), column.type)}
                  </td>
                )
: (
                  <td key={column.key}></td>
                )
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
